import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { sendPushNotification } from '@/lib/utils';
import { Bell, Send, TestTube, Users } from 'lucide-react';

export function PushNotificationTest() {
  const [title, setTitle] = useState('Git Academy Ծանուցում');
  const [message, setMessage] = useState('Սա փորձարկման ծանուցում է');
  const [userIds, setUserIds] = useState('');
  const [targetGroup, setTargetGroup] = useState<string>('all');
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  const handleSendTest = async () => {
    if (!title.trim() || !message.trim()) {
      toast({
        title: "Սխալ",
        description: "Խնդրում ենք լրացնել վերնագիր և հաղորդագրություն",
        variant: "destructive"
      });
      return;
    }

    if (targetGroup === 'manual' && !userIds.trim()) {
      toast({
        title: "Սխալ",
        description: "Խնդրում ենք նշել օգտատերերի ID-ները կամ ընտրել այլ խումբ",
        variant: "destructive"
      });
      return;
    }

    setSending(true);
    
    try {
      let userIdArray: string[] | undefined;
      let targetRole: string | undefined;

      if (targetGroup === 'manual') {
        userIdArray = userIds.split(',').map(id => id.trim()).filter(Boolean);
      } else if (targetGroup !== 'all') {
        targetRole = targetGroup;
      }

      console.log('🧪 Testing push notification:', { title, message, targetGroup, userIdArray, targetRole });

      const result = await sendPushNotification(
        title,
        message,
        userIdArray,
        {
          type: 'test_notification',
          tag: 'admin-test',
          url: '/admin/dashboard',
          targetRole
        }
      );

      if (result.success) {
        toast({
          title: "✅ Հաջողություն",
          description: `Push ծանուցումը ուղարկվել է: ${result.data?.sent || 0} հաջող, ${result.data?.failed || 0} անհաջող`,
        });
      } else {
        toast({
          title: "❌ Սխալ",
          description: `Push ծանուցման ուղարկումը ձախողվեց: ${result.error?.message || 'Անհայտ սխալ'}`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('💥 Push notification test error:', error);
      toast({
        title: "💥 Սխալ",
        description: "Push ծանուցման փորձարկումը ձախողվեց",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2">
          <TestTube className="h-5 w-5" />
          Push Ծանուցումների Փորձարկում
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 px-4 sm:px-6">
        <div className="space-y-2">
          <Label htmlFor="title">Վերնագիր</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ծանուցման վերնագիր"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">Հաղորդագրություն</Label>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ծանուցման բովանդակություն"
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="targetGroup" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Ում ուղարկել
          </Label>
          <Select value={targetGroup} onValueChange={setTargetGroup}>
            <SelectTrigger>
              <SelectValue placeholder="Ընտրել խումբը" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Բոլորին</SelectItem>
              <SelectItem value="admin">Ադմիններ</SelectItem>
              <SelectItem value="lecturer">Դասախոսներ</SelectItem>
              <SelectItem value="student">Ուսանողներ</SelectItem>
              <SelectItem value="manual">Ձեռքով (User IDs)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {targetGroup === 'manual' && (
          <div className="space-y-2">
            <Label htmlFor="userIds">
              Օգտատիրոջ ID-ներ
              <span className="text-sm text-muted-foreground ml-2">
                Գրել ստորակետով բաժանված
              </span>
            </Label>
            <Input
              id="userIds"
              value={userIds}
              onChange={(e) => setUserIds(e.target.value)}
              placeholder="օր․ uuid1, uuid2, uuid3"
            />
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button 
            onClick={handleSendTest}
            disabled={sending}
            className="min-h-11 w-full justify-center whitespace-normal sm:w-auto sm:justify-start"
          >
            {sending ? (
              <>
                <Bell className="h-4 w-4 animate-pulse" />
                Ուղարկվում է...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Ուղարկել փորձարկման ծանուցում
              </>
            )}
          </Button>
        </div>

        <div className="bg-muted p-3 rounded-md text-sm">
          <h4 className="font-medium mb-2">💡 Ծանուցում:</h4>
          <ul className="space-y-1 text-muted-foreground">
            <li>• Push ծանուցումները կուղարկվեն միայն այն օգտատերերին, ովքեր subscribe են արել</li>
            <li>• Ծանուցումները կաշխատեն միայն HTTPS միջավայրում</li>
            <li>• Mobile սարքերում PWA install անելուց հետո badge-ները ավելի լավ կաշխատեն</li>
            <li>• Service Worker-ը պիտի active լինի push ծանուցումներ ստանալու համար</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}